import { useState } from "react";
import { AlertDialog, DialogContainer } from "@react-spectrum/s2";

import { OUTDATED_ACTION } from "./variationDependencies";

/**
 * Catches an outdated variation up in one go — what its `action` asks for
 * (see `getOutdatedAction`):
 *
 * - a new image: `onKeepPrompt` regenerates it from the prompt as it is (a
 *   static prompt is refreshed by the pipeline while generating);
 * - a new prompt and image: `regenerateFromCampaign`. A prompt the user wrote
 *   (`isPromptEdited`) is not overwritten without asking: the returned
 *   `confirmDialog` offers to overwrite it, or to keep it (`onKeepPrompt`).
 *
 * Render `confirmDialog` wherever the hook is used. `editedCount` words it for
 * several edited prompts at once.
 */
export const useOutdatedUpdate = ({
  action,
  isPromptEdited = false,
  editedCount = 1,
  regenerateFromCampaign,
  onKeepPrompt,
}) => {
  const [isConfirming, setConfirming] = useState(false);

  const update = () => {
    if (action === OUTDATED_ACTION.IMAGE) {
      onKeepPrompt?.();
    } else if (action === OUTDATED_ACTION.PROMPT_AND_IMAGE) {
      if (isPromptEdited) {
        setConfirming(true);
      } else {
        regenerateFromCampaign?.();
      }
    }
  };

  const confirmDialog = (
    <DialogContainer onDismiss={() => setConfirming(false)}>
      {isConfirming && (
        <AlertDialog
          title={
            editedCount > 1
              ? "Overwrite your prompts?"
              : "Overwrite your prompt?"
          }
          variant="warning"
          primaryActionLabel="Overwrite"
          secondaryActionLabel={
            editedCount > 1 ? "Keep prompts" : "Keep prompt"
          }
          cancelLabel="Cancel"
          onPrimaryAction={() => {
            setConfirming(false);
            regenerateFromCampaign?.();
          }}
          onSecondaryAction={() => {
            setConfirming(false);
            onKeepPrompt?.();
          }}
          onCancel={() => setConfirming(false)}
        >
          {editedCount > 1
            ? `The prompts of ${editedCount} images were edited by hand. Updating writes new prompts from the campaign, replacing your edits. You can also keep your prompts and only generate new images.`
            : "The prompt of this image was edited by hand. Updating writes a new prompt from the campaign, replacing your edits. You can also keep your prompt and only generate a new image."}
        </AlertDialog>
      )}
    </DialogContainer>
  );

  return { update, confirmDialog };
};
